import React, { useCallback, useEffect } from 'react';

import { VIDEO_ANALYTIC_TYPE_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { Control, Tooltip } from '@vlabs/uikit';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CustomFilterPopover } from '@vlabs/shared/components/filters-panel/components/CustomFilterPopover';
import { ScoreFieldsPopover } from '@vlabs/shared/components/filters-panel/va/ScoreFieldsPopover';
import { ScoreRadioFieldsPopover } from '@vlabs/shared/components/filters-panel/va/ScoreRadioFieldsPopover';

import { isEqualValue, isSelected, toggleOption } from '../helpers';
import { useFilterCount } from '../hooks';
import st from './EventTypesCheckboxesPopover.module.sass';

export function EventTypesCheckboxesPopover({ scoreFields, onCountChange = undefined, facecoverVersion = 'v1' }) {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  const allScoreFieldNames = Object.values(scoreFields).flatMap((f) => [f.gte, f.lt]);
  const watchedScores = useWatch({ name: allScoreFieldNames }) || [];
  const selectedEventTypes = useWatch({ name: 'event_type' }) || [];
  const scoreEntries = allScoreFieldNames.map((fieldName, index) => ({
    fieldName,
    value: watchedScores[index],
  }));

  const filterCount = selectedEventTypes.length > 0 ? 1 : 0;
  useFilterCount('event_type', filterCount, onCountChange);

  const filledScoreKey = Object.entries(scoreFields).find(([, score]) => {
    const gte = scoreEntries.find((e) => e.fieldName === score.gte)?.value;
    const lt = scoreEntries.find((e) => e.fieldName === score.lt)?.value;

    const isFilled = (val) => {
      if (typeof val === 'number') return true;
      if (typeof val === 'string') return val.trim() !== '';
      return false;
    };

    return isFilled(gte) || isFilled(lt);
  })?.[0];

  const onToggle = useCallback((safeArray, option, onChange) => {
    onChange(toggleOption(safeArray, option));

    if (isSelected(safeArray, option) && scoreFields[option.value]) {
      const { gte, lt } = scoreFields[option.value];
      setValue(gte, '');
      setValue(lt, '');
    }
  }, [setValue]);

  // если у одного из типов событий заполнен порог (filledScoreKey)
  // то оставляем выбранным только этот тип события
  useEffect(() => {
    if (filledScoreKey) {
      // конкретный тип события, допустимый сейчас
      const allowedEventType = VIDEO_ANALYTIC_TYPE_OPTIONS.raw.find((o) => o.value === filledScoreKey);
      // из всех выбранных оставлены только допустимые
      const allowedOnlySelected = selectedEventTypes.filter((v) => v.value === allowedEventType?.value);

      if (!isEqualValue(allowedOnlySelected, selectedEventTypes)) {
        setValue('event_type', allowedOnlySelected);
      }
    }
  }, [filledScoreKey, setValue]);

  return (
    <CustomFilterPopover
      hasFilterValue={selectedEventTypes.length > 0}
      testId="eventTypesFilter"
      triggerValue={t('types of events')}
    >
      <Controller
        control={control}
        name="event_type"
        render={({ field }) => {
          const { value, onChange } = field;
          const safeArray = Array.isArray(value) ? value : [];
          return (
            <div className={st.Root}>
              <div className="Subtitle-1">{t('show events')}</div>

              {VIDEO_ANALYTIC_TYPE_OPTIONS.raw.map((option) => {
                const isOptionSelected = isSelected(safeArray, option);
                const hasScores = scoreFields[option.value];
                const isFacecover = option.value === 'facecover';
                const isFacecoverV2 = isFacecover && facecoverVersion === 'v2';
                const isDisabled = filledScoreKey && (filledScoreKey !== option.value);
                return (
                  <Tooltip
                    className={st.Tooltip}
                    key={option.value}
                    tooltipContent={!!isDisabled && t('videoAnalytics:archivePage.filters.score.tooltip')}
                  >
                    <div className={st.Item}>
                      <Control.Checkbox
                        checked={isOptionSelected}
                        disabled={isDisabled}
                        id={`event_type.${option.value}`}
                        label={option.label}
                        onChange={() => onToggle(safeArray, option, onChange)}
                      />

                      {isFacecoverV2 ? (
                        <ScoreRadioFieldsPopover
                          disabled={!isOptionSelected || isDisabled}
                          id="facecoverScoreRadioRange"
                          keys={['niqab', 'balaclava', 'other']}
                          radioName="facecover_subtype"
                          scoreFields={scoreFields}
                          triggerLabelKey={scoreFields.facecover?.labelKey}
                        />
                      ) : (
                        !!hasScores && (
                        <ScoreFieldsPopover
                          disabled={!isOptionSelected || isDisabled}
                          scoreFields={scoreFields[option.value]}
                        />
                        )
                      )}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          );
        }}
      />
    </CustomFilterPopover>
  );
}
